import { memoize } from "./memoize";

export type Env = "development" | "staging" | "production";

// Used to show component `debug` messages on while debugging
export const shouldShowDebug = memoize(() => isLocalViteEnv());

export const LOCAL_DOMAINS = ["local.moov.io"] as const;
export const STAGING_DOMAINS = [
  "moov-staging.io",
  "moov-staging.money",
  "moov-staging.link"
] as const;
export const PRODUCTION_DOMAINS = ["moov.io", "moov.money", "moov.link"] as const;

const isLocalHostname = (hostname: string) =>
  hostname === "localhost" || LOCAL_DOMAINS.some((domain) => hostname.endsWith(domain));
const isLocalViteEnv = memoize(() => import.meta.env.DEV);
const isMockingEnabled = memoize(() => import.meta.env.VITE_MSW_BROWSER_MOCKING === "1");
const isStagingHostname = memoize((hostname: string) =>
  STAGING_DOMAINS.some((domain) => hostname.endsWith(domain))
);

const envFromHostname = (hostname: string): Env =>
  isLocalHostname(hostname)
    ? "development"
    : isStagingHostname(hostname)
      ? "staging"
      : "production";

const nonMemoCalculateEnvFromHostname = (shouldDisplayWarning = true) => {
  const { hostname } = window.location;
  const environment = envFromHostname(hostname);
  if (environment !== "production" && shouldDisplayWarning) {
    console.warn(
      "🟠 No environment detected through Vite env variables, falling back to URL matching only"
    );
  }
  if (environment !== "production") {
    console.log(
      "ℹ️  Using URL matching to determine environment. This mode IS compatible with the Development Extension"
    );
  }
  return environment;
};
const calculateEnvFromHostname = memoize(nonMemoCalculateEnvFromHostname);

/**
 * Canonical environment detection for all monorepo apps
 *
 * 🟠 It works best with apps built with Vite
 *
 * If the required variables to determine the environment are not available,
 * it will make a best effort guess based on the hostname, and will log a warning in the console
 *
 * @param {boolean} urlOnlyEnvResolution - If true, only the URL will be used to determine the environment. This is useful for apps tested through the Development Extension
 * @returns {string} - The environment name
 */
export const getEnv = memoize((urlOnlyEnvResolution = false): Env => {
  // import.meta.env.DEV is replaced at BUILD TIME (npm run build) by Vite with a boolean
  // indicating if the environment is development. Therefore it may be `undefined` if the app
  // is not built with Vite
  //
  // I was unsure if it might also throw an exception at runtime if the app was
  // not built with Vite, so being extra cautious and using a try/catch
  //
  // https://vitejs.dev/guide/env-and-mode.html#modes
  try {
    if (urlOnlyEnvResolution) {
      return calculateEnvFromHostname(!urlOnlyEnvResolution);
    }
    // Because Vite env will be "production" when the bundle is built, we can not conclude what the environment is only from the Vite env when
    // the Vite env is !== DEV (i.e. when !isLocalViteEnv()), therefore when that is false (we're for sure not in vite DEV mode), we continue resolving the
    // environment based on the URL, which might end up being "development", as the app may be running in p-dev, which is considered "development"
    return isLocalViteEnv() ? "development" : calculateEnvFromHostname();
  } catch (_) {
    return calculateEnvFromHostname();
  }
});

export const isDevelopment = memoize(() => getEnv() === "development");
export const isProduction = memoize(() => getEnv() === "production");
export const isStaging = memoize(() => getEnv() === "staging");
export const shouldEnableMocks = memoize(() => isMockingEnabled() && isDevelopment());
export const isLocalWithStagingBE = memoize(
  () => isLocalViteEnv() && import.meta.env.VITE_BE_ENV === "staging" && !isDevelopmentExtension()
);
/**
 * Returns the environment of the back-end, which is either "local", "production" or "staging"
 */
export const getBEEnv = memoize(() => {
  // When local proxy is pointing to staging
  if (isLocalWithStagingBE()) return "staging";
  // When we run locally but under staging or production URLs through the Development Extension
  if (isDevelopmentExtension()) {
    // "production" or "staging"
    return getEnv(true);
  }
  // If none of the above, we're running locally against local p-dev
  return "local";
});
// Vite is in dev mode but the URL matches staging or production (Development Extension)
export const isDevelopmentExtension = memoize(
  () =>
    isLocalViteEnv() &&
    // we calculate the environment according to URL only
    (calculateEnvFromHostname(false) === "production" ||
      calculateEnvFromHostname(false) === "staging")
);
// For testing purposes only
export const __test = {
  nonMemoCalculateEnvFromHostname
};

if (isDevelopmentExtension()) {
  console.log("🟠 Development Extension is enabled: Client being served from local");
}

if (isLocalWithStagingBE()) {
  console.log(
    "🟠 Local client directed at STAGING back-end. Enable VITE_PROXY_LOGGING for accurate Req/Res headers"
  );
}
